'use client';

import type { ApiKey } from '@interfaces/apiKey';
import styles from '@styles/ApiKeys/ApiKeys.module.scss';
import { Alert, Modal, Typography } from 'antd';
import { keyLabel } from './keyLabel';

/**
 * Revocation takes effect on the next request — nothing about key validity is
 * cached — so this dialog names the key it is about to break rather than asking
 * a generic "are you sure?".
 */

type Props = {
  apiKey: ApiKey | null;
  isRevoking: boolean;
  errorMessage: string | null;
  onConfirm: (apiKey: ApiKey) => void;
  onCancel: () => void;
};

const RevokeKeyModal: React.FC<Props> = ({ apiKey, isRevoking, errorMessage, onConfirm, onCancel }) => (
  <Modal
    open={apiKey !== null}
    title={apiKey === null ? 'Revoke key' : `Revoke “${keyLabel(apiKey)}”?`}
    okText="Revoke key"
    cancelText="Keep key"
    okButtonProps={{ color: 'danger', variant: 'solid' }}
    confirmLoading={isRevoking}
    maskClosable={!isRevoking}
    onOk={() => apiKey !== null && onConfirm(apiKey)}
    onCancel={onCancel}
  >
    {apiKey !== null && (
      <>
        <Typography.Paragraph>
          Any integration using <strong>{keyLabel(apiKey)}</strong> (
          <span className={styles.keyPrefix}>{apiKey.prefix}…</span>) stops working on its very next request. This
          cannot be undone — a replacement key is a new key, with a new value to deploy.
        </Typography.Paragraph>

        <Typography.Paragraph className={styles.muted}>
          Rotating rather than retiring? Create the new key, deploy it, and revoke this one afterwards, so there is no
          gap in which neither works.
        </Typography.Paragraph>

        {errorMessage !== null && (
          <Alert type="error" showIcon title="The key was not revoked" description={errorMessage} />
        )}
      </>
    )}
  </Modal>
);

export { RevokeKeyModal };
